import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { APP_TYPES, AppType, SubscriptionAppType } from './platform';
import { COOKIE_SESSION_TOKEN } from './session-token';

export interface PlatformUser {
  id: string;
  email: string;
  name?: string;
  phone?: string | null;
  role: 'admin' | 'user';
  memberCategory?: 'platinum' | 'paid' | 'trial';
  allowedApps: AppType[];
  appSubscriptions?: AppSubscriptionStatus[];
  staticIp?: string | null;
  isActive?: boolean;
  twoFactorEnabled?: boolean;
}

export interface AppSubscriptionStatus {
  appType: SubscriptionAppType;
  subscriptionEndDate: string | null;
  isEnabled: boolean;
  isActive: boolean;
}

interface AuthStore {
  user: PlatformUser | null;
  token: string | null;
  clientSessionId: string | null;
  setAuth: (user: PlatformUser, token?: string | null) => void;
  setUser: (user: PlatformUser | null) => void;
  logout: () => void;
}

export const PLATFORM_AUTH_STORAGE_KEY = 'bluechip-platform-auth';
export const TRADE_SCALPER_DASHBOARD_STATE_KEY = 'trade-scalper-dashboard-state';

function createClientSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeAllowedApps(value: unknown): AppType[] {
  if (!Array.isArray(value) || value.length === 0) {
    return ['trade_bridge'];
  }

  const normalized = Array.from(new Set(
    value.map(item => String(item).trim()).filter((item): item is AppType => (
      (APP_TYPES as readonly string[]).includes(item)
    )),
  ));

  return normalized.length > 0 ? normalized : ['trade_bridge'];
}

function normalizeUser(user: PlatformUser): PlatformUser {
  return {
    ...user,
    allowedApps: normalizeAllowedApps(user.allowedApps),
  };
}

function writePersistedAuth(user: PlatformUser | null, token: string | null, clientSessionId: string | null): void {
  if (typeof window === 'undefined') return;

  if (!user || !token) {
    localStorage.removeItem(PLATFORM_AUTH_STORAGE_KEY);
    localStorage.removeItem(TRADE_SCALPER_DASHBOARD_STATE_KEY);
    return;
  }

  localStorage.setItem(PLATFORM_AUTH_STORAGE_KEY, JSON.stringify({
    state: {
      user: normalizeUser(user),
      token,
      clientSessionId,
    },
    version: 0,
  }));
}

export const useAuthStore = create<AuthStore>()(
  persist(
    set => ({
      user: null,
      token: null,
      clientSessionId: null,
      setAuth: (user, token) => {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('access_token');
        }
        const normalizedUser = normalizeUser(user);
        const sessionToken = token || COOKIE_SESSION_TOKEN;
        const clientSessionId = createClientSessionId();
        writePersistedAuth(normalizedUser, sessionToken, clientSessionId);
        set({ user: normalizedUser, token: sessionToken, clientSessionId });
      },
      setUser: user => {
        const token = useAuthStore.getState().token;
        const clientSessionId = useAuthStore.getState().clientSessionId;
        const normalizedUser = user ? normalizeUser(user) : null;
        writePersistedAuth(normalizedUser, token, clientSessionId);
        set({ user: normalizedUser });
      },
      logout: () => {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('access_token');
        }
        writePersistedAuth(null, null, null);
        set({ user: null, token: null, clientSessionId: null });
      },
    }),
    {
      name: PLATFORM_AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
